import { ExternalLink } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface CountItem {
  value: number;
  label: string;
  icon?: ReactNode;
}

interface StatsBoxProps {
  icon: ReactNode;
  count?: number | string;
  countLabel?: string;
  /** For dual count display (e.g., dogs and cats separately) */
  counts?: CountItem[];
  title: string;
  subtitle: string;
  onClick?: () => void;
  isLoading?: boolean;
  className?: string;
  /** Optional small secondary count rendered under the subtitle. */
  subCount?: {
    value: number;
    label: string;
    isLoading?: boolean;
  };
}

const StatsBox = ({
  icon,
  count,
  countLabel,
  counts,
  title,
  subtitle,
  onClick,
  isLoading = false,
  className = "",
  subCount,
}: StatsBoxProps) => {
  const Wrapper = onClick ? "button" : "div";
  const { t } = useTranslation("common");

  return (
    <Wrapper
      onClick={onClick}
      className={`w-full h-full bg-hero-gradient text-golden backdrop-blur-sm rounded-3xl p-6 shadow-lg transition-all duration-300 hover:shadow-2xl group border border-lighter-blue relative overflow-hidden ${
        onClick ? "cursor-pointer hover:-translate-y-1" : ""
      } ${className}`}
    >
      {/* Gradient background on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-tabby-light to-soft-pink opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none" />
      {onClick && (
        <ExternalLink className="absolute top-4 right-4 text-golden/80 group-hover:text-golden transition-colors duration-300" />
      )}
      <div className="relative z-10 flex flex-col items-center">
        {/* Icon */}
        <div className="text-golden group-hover:text-deep-umber transition-colors duration-300 mb-3 flex justify-center">
          {icon}
        </div>
        {/* Title */}
        <h3 className="text-xl font-bold text-golden mb-2 group-hover:text-tabby-orange transition-colors duration-300">
          {title}
        </h3>

        {/* Count and Label (if provided) - Single count */}
        {count !== undefined && !counts && (
          <div className="mb-2">
            {isLoading ? (
              <span className="animate-pulse text-xl font-semibold text-golden">
                {t("nav.loadingShort")}
              </span>
            ) : (
              <div className="flex items-end gap-2 justify-center">
                <span className="text-4xl font-bold text-golden">
                  {typeof count === "number" ? count.toLocaleString() : count}
                </span>
                {countLabel && (
                  <span className="text-xs pb-1 font-semibold text-golden/80 group-hover:text-tabby-orange transition-colors duration-300">
                    {countLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Dual count display (e.g., dogs and cats) */}
        {counts && counts.length > 0 && (
          <div className="mb-2">
            {isLoading ? (
              <span className="animate-pulse text-xl font-semibold text-golden">
                {t("nav.loadingShort")}
              </span>
            ) : (
              <div className="flex items-center gap-6 justify-center">
                {counts.map((item, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className="flex items-end gap-2">
                      {item.icon && (
                        <span className="text-golden/80 mb-1">{item.icon}</span>
                      )}
                      <span className="text-3xl font-bold text-golden">
                        {item.value.toLocaleString()}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-golden/80 group-hover:text-tabby-orange transition-colors duration-300">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Subtitle */}
        <p className="text-golden/60 text-md inline-flex gap-1 leading-relaxed group-hover:text-golden/80 transition-colors duration-300">
          {subtitle}
        </p>

        {/* Optional secondary count (e.g. particulares sub-count) */}
        {subCount && (
          <p className="mt-1 text-xs font-semibold text-golden/70 group-hover:text-golden transition-colors duration-300">
            {subCount.isLoading ? (
              <span className="animate-pulse">…</span>
            ) : (
              <>
                {subCount.value.toLocaleString()} {subCount.label}
              </>
            )}
          </p>
        )}
      </div>

      {/* Hover shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none" />
    </Wrapper>
  );
};

export default StatsBox;
